/**
 * ✨🦸 AwakeningRevealCinematic — full-screen bloom + slow name reveal.
 *
 * Discovery-first payoff: when a hero's Awakening trigger finally fires in
 * play, this component runs a dramatic screen-fill so the discovery *lands*.
 * Timeline:
 *   0.0s  black flash + zoom
 *   0.4s  silhouette pulse
 *   0.9s  glyph clarifies + accent bloom
 *   1.6s  codename types out ("T H E   B L O O D — P R A C T I T I O N E R")
 *   2.4s  tagline fades in
 *   3.6s  "Tap to continue" prompt
 *
 * Trigger surface — an imperative event bus so ANY component can fire the
 * reveal without prop-drilling.
 *
 *   import { fireAwakeningReveal } from "@/components/AwakeningRevealCinematic";
 *   fireAwakeningReveal({ hero_id: "blood_practitioner" });
 *
 * The provider is mounted once in App.js and listens on `window` for the
 * event. It fetches the awakening entry from `/api/awakenings/{hero_id}`
 * so it always shows the latest art + tagline the canon has.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT = "omnimuscle:awakening-reveal";

/** Fire the cinematic from anywhere. Idempotent — spamming the same
 *  hero_id while a reveal is on-screen is ignored by the provider. */
export function fireAwakeningReveal({ hero_id }) {
  if (!hero_id) return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { hero_id } }));
}

export default function AwakeningRevealCinematic() {
  const [entry, setEntry] = useState(null);
  const [phase, setPhase] = useState("hidden"); // "hidden" | "flash" | "bloom" | "name" | "tagline" | "ready"
  const [busy, setBusy]   = useState(false);

  const dismiss = useCallback(() => {
    setPhase("hidden");
    setEntry(null);
    setBusy(false);
  }, []);

  useEffect(() => {
    const handler = async (e) => {
      const hero_id = e?.detail?.hero_id;
      if (!hero_id || busy) return;
      setBusy(true);
      try {
        const r = await axios.get(`${API}/awakenings/${hero_id}`);
        // Endpoint returns { ok, item } — unwrap to the entry.
        const item = r.data?.item || r.data;
        if (!item?.hero_id) throw new Error("awakening not found");
        setEntry(item);
        setPhase("flash");
      } catch {
        setBusy(false);   // silent — no cinematic if the hero isn't in the registry
      }
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, [busy]);

  // Timeline
  useEffect(() => {
    if (!entry || phase === "hidden") return;
    const timers = [
      setTimeout(() => setPhase("bloom"),   400),
      setTimeout(() => setPhase("name"),    900),
      setTimeout(() => setPhase("tagline"), 2400),
      setTimeout(() => setPhase("ready"),   3600),
    ];
    return () => timers.forEach(clearTimeout);
  }, [entry, phase === "flash"]); // restart only on flash-arm

  if (!entry) return null;

  const accent = entry.accent || "#D4AF37";
  const codename = (entry.title || entry.base_name || "").toUpperCase().split("").join(" ");
  const showName    = ["name", "tagline", "ready"].includes(phase);
  const showTagline = ["tagline", "ready"].includes(phase);
  const showDismiss = phase === "ready";

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-6"
      data-testid="awakening-reveal-cinematic"
      onClick={showDismiss ? dismiss : undefined}
      style={{
        background:
          phase === "flash"
            ? "radial-gradient(circle at 50% 50%, #FFF 0%, #000 30%)"
            : `radial-gradient(circle at 50% 50%, ${accent}25 0%, #000 55%, #000 100%)`,
        transition: "background 0.4s ease",
      }}
    >
      {/* grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-40"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0px, transparent 1px, rgba(255,255,255,0.06) 1px, rgba(255,255,255,0.06) 2px)",
        }}
      />
      {/* accent bloom rings */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 45%, ${accent}55 0%, transparent 30%)`,
          opacity: phase === "flash" ? 0 : 1,
          transition: "opacity 0.9s ease",
        }}
      />

      {/* Content stack */}
      <div className="relative z-10 text-center max-w-2xl">
        {/* Silhouette / art */}
        <div
          className="relative mx-auto mb-6 transition-all duration-[900ms]"
          style={{
            width: 220,
            height: 220,
            filter:
              phase === "flash"     ? "blur(24px) brightness(2) saturate(0)"
            : phase === "bloom"     ? "blur(6px) brightness(1.4)"
            :                         "blur(0px) brightness(1.05)",
            transform:
              phase === "flash" ? "scale(0.85)" : "scale(1.0)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              boxShadow: `0 0 120px ${accent}CC, inset 0 0 60px ${accent}66`,
              borderRadius: "50%",
            }}
          />
          {entry.art_url && (
            <img
              src={entry.art_url}
              alt=""
              className="relative w-full h-full object-cover rounded-full border-2"
              style={{ borderColor: `${accent}AA` }}
            />
          )}
        </div>

        {/* Awakening prefix */}
        <div
          className="text-[10px] uppercase tracking-[0.5em] font-black mb-2 transition-opacity duration-[700ms]"
          style={{ color: accent, opacity: showName ? 1 : 0 }}
        >
          Awakening Unlocked
        </div>

        {/* Codename — letter-spaced typing effect */}
        <h1
          className="font-display text-2xl sm:text-3xl md:text-4xl tracking-[0.15em] leading-tight text-white transition-all duration-[900ms]"
          style={{
            textShadow: `0 0 24px ${accent}88`,
            opacity: showName ? 1 : 0,
            transform: showName ? "translateY(0)" : "translateY(20px)",
          }}
          data-testid="awakening-reveal-name"
        >
          {codename}
        </h1>

        {/* Tagline */}
        <p
          className="text-sm sm:text-base italic text-neutral-300 mt-4 leading-relaxed max-w-lg mx-auto transition-opacity duration-[900ms]"
          style={{ opacity: showTagline ? 1 : 0 }}
          data-testid="awakening-reveal-tagline"
        >
          {entry.tagline}
        </p>

        {/* Dismiss prompt */}
        <div
          className="mt-10 text-[10px] uppercase tracking-widest text-neutral-500 transition-opacity duration-[700ms]"
          style={{ opacity: showDismiss ? 1 : 0 }}
        >
          Tap anywhere to continue
        </div>
      </div>

      {/* Corner close button (accessibility escape) */}
      {showDismiss && (
        <button
          onClick={dismiss}
          data-testid="awakening-reveal-dismiss"
          className="absolute top-4 right-4 text-neutral-400 hover:text-white p-2"
          aria-label="Dismiss reveal"
        >
          <X size={22} />
        </button>
      )}
    </div>
  );
}
